import { api } from "@/lib/api";
import {
  clearRememberedActiveInterviewRoute,
  getRememberedActiveInterviewRoute,
  getRememberedActiveInterviewSessionId,
} from "@/lib/activeInterviewNavigation";

const SETUP_PATH = "/upload";
const GUARD_ID = "active-interview-setup-guard";
const RECOVERY_POLL_MS = 5000;
let checking = false;
let installed = false;
let recoveryPollTimer = null;

export const COPY = {
  en: {
    checking: "Checking for an active interview",
    title: "Interview already in progress",
    body: "An interview is already running in another browser tab or window. Return to that window to continue. You cannot start a new interview while it remains active.",
    recovery: "If the previous browser closed or crashed unexpectedly, keep this page open. We’ll check automatically and return you to Interview Session Setup after the abandoned interview is cleared. If the normal close signal could not be delivered, this can take up to about one minute.",
    status: "Checking automatically…",
  },
  "pt-BR": {
    checking: "Verificando se há uma entrevista ativa",
    title: "Entrevista já em andamento",
    body: "Uma entrevista já está em andamento em outra guia ou janela do navegador. Volte para essa janela para continuar. Você não pode iniciar uma nova entrevista enquanto a atual estiver ativa.",
    recovery: "Se o navegador anterior foi fechado ou travou inesperadamente, mantenha esta página aberta. Verificaremos automaticamente e retornaremos você à Configuração da Sessão de Entrevista quando a entrevista abandonada for liberada. Se o sinal normal de fechamento não pôde ser enviado, isso pode levar até cerca de um minuto.",
    status: "Verificando automaticamente…",
  },
  "es-419": {
    checking: "Comprobando si hay una entrevista activa",
    title: "Ya hay una entrevista en curso",
    body: "Ya hay una entrevista en curso en otra pestaña o ventana del navegador. Vuelva a esa ventana para continuar. No puede iniciar una nueva entrevista mientras la actual siga activa.",
    recovery: "Si el navegador anterior se cerró o falló inesperadamente, mantenga esta página abierta. Comprobaremos automáticamente y volveremos a la Configuración de la sesión de entrevista cuando se libere la entrevista abandonada. Si no se pudo enviar la señal normal de cierre, esto puede tardar hasta aproximadamente un minuto.",
    status: "Comprobando automáticamente…",
  },
  "fr-CA": {
    checking: "Vérification d’une entrevue active",
    title: "Une entrevue est déjà en cours",
    body: "Une entrevue est déjà en cours dans un autre onglet ou une autre fenêtre du navigateur. Retournez à cette fenêtre pour continuer. Vous ne pouvez pas commencer une nouvelle entrevue tant que celle-ci est active.",
    recovery: "Si le navigateur précédent a été fermé ou a cessé de fonctionner de façon inattendue, gardez cette page ouverte. Nous vérifierons automatiquement et vous ramènerons à la Configuration de la séance d’entrevue lorsque l’entrevue abandonnée aura été libérée. Si le signal de fermeture normal n’a pas pu être envoyé, cela peut prendre jusqu’à environ une minute.",
    status: "Vérification automatique…",
  },
};

function normalizedPath() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function normalizeLanguage(value) {
  const raw = String(value || "en").trim().toLowerCase();
  if (raw.startsWith("pt")) return "pt-BR";
  if (raw.startsWith("es")) return "es-419";
  if (raw.startsWith("fr")) return "fr-CA";
  return "en";
}

function ensureGuard() {
  let guard = document.getElementById(GUARD_ID);
  if (!guard) {
    guard = document.createElement("div");
    guard.id = GUARD_ID;
    guard.style.cssText = [
      "position:fixed",
      "inset:0",
      "z-index:2147483647",
      "background:#fff",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "padding:24px",
    ].join(";");
    document.body.appendChild(guard);
  }

  if (!document.getElementById(`${GUARD_ID}-style`)) {
    const style = document.createElement("style");
    style.id = `${GUARD_ID}-style`;
    style.textContent = "@keyframes activeInterviewGuardSpin{to{transform:rotate(360deg)}}";
    document.head.appendChild(style);
  }
  return guard;
}

function showCheckingGuard() {
  const copy = COPY.en;
  const guard = ensureGuard();
  guard.setAttribute("role", "status");
  guard.setAttribute("aria-live", "polite");
  guard.setAttribute("aria-label", copy.checking);
  guard.innerHTML = '<span aria-hidden="true" style="width:28px;height:28px;border:3px solid #dbe4ef;border-top-color:#073b75;border-radius:50%;animation:activeInterviewGuardSpin .8s linear infinite"></span>';
}

function showActiveGuard(data) {
  const lang = normalizeLanguage(data?.interface_language);
  const copy = COPY[lang] || COPY.en;
  const guard = ensureGuard();
  guard.setAttribute("role", "main");
  guard.removeAttribute("aria-live");
  guard.removeAttribute("aria-label");
  guard.setAttribute("data-testid", "active-interview-blocked");
  guard.innerHTML = `
    <div style="width:100%;max-width:620px;text-align:center;color:#1f2933;font-family:Inter,Arial,sans-serif">
      <img src="/brand/globe-logo.svg" alt="" aria-hidden="true" style="width:86px;height:63px;object-fit:contain;margin:0 auto 20px" />
      <div style="font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#073b75;margin-bottom:18px">English Job Interview Simulator</div>
      <h1 style="font-size:30px;line-height:1.15;margin:0;font-weight:600;letter-spacing:-.02em">${copy.title}</h1>
      <p style="font-size:16px;line-height:1.65;margin:20px auto 0;max-width:560px;color:#5f6b76">${copy.body}</p>
      <div style="margin:28px auto 0;padding:18px 20px;border:1px solid #d8d5cd;border-radius:16px;background:#f8f7f3;max-width:560px">
        <p style="font-size:14px;line-height:1.6;margin:0;color:#48535d">${copy.recovery}</p>
        <div role="status" aria-live="polite" style="display:flex;align-items:center;justify-content:center;gap:10px;margin-top:16px;font-size:13px;color:#073b75">
          <span aria-hidden="true" style="width:16px;height:16px;border:2px solid #dbe4ef;border-top-color:#073b75;border-radius:50%;animation:activeInterviewGuardSpin .8s linear infinite"></span>
          <span>${copy.status}</span>
        </div>
      </div>
    </div>`;
}

function hideGuard() {
  document.getElementById(GUARD_ID)?.remove();
}

function stopRecoveryPolling() {
  if (recoveryPollTimer) {
    window.clearInterval(recoveryPollTimer);
    recoveryPollTimer = null;
  }
}

function resumeRememberedInterviewIfSameSession(data) {
  if (!data?.active || !data?.session_id) return false;
  const rememberedId = getRememberedActiveInterviewSessionId();
  const rememberedRoute = getRememberedActiveInterviewRoute();
  if (!rememberedId || !rememberedRoute || String(rememberedId) !== String(data.session_id)) return false;

  stopRecoveryPolling();
  hideGuard();
  window.location.replace(rememberedRoute);
  return true;
}

function startRecoveryPolling() {
  if (recoveryPollTimer) return;

  const poll = async () => {
    if (normalizedPath() !== SETUP_PATH) {
      stopRecoveryPolling();
      hideGuard();
      return;
    }
    try {
      const { data } = await api.get("/active-interview");
      if (!data?.active) {
        clearRememberedActiveInterviewRoute();
        stopRecoveryPolling();
        window.location.reload();
        return;
      }
      if (resumeRememberedInterviewIfSameSession(data)) return;
      if (getRememberedActiveInterviewSessionId()) clearRememberedActiveInterviewRoute();
      showActiveGuard(data);
    } catch (error) {
      if (error?.response?.status === 401) {
        stopRecoveryPolling();
        window.location.reload();
      }
      // A temporary network error leaves the protective screen in place. The
      // next poll retries automatically and POST /sessions remains protected by
      // the backend concurrency guard.
    }
  };

  recoveryPollTimer = window.setInterval(poll, RECOVERY_POLL_MS);
}

export async function checkActiveInterviewBeforeSetup() {
  if (typeof window === "undefined" || typeof document === "undefined") return true;
  if (normalizedPath() !== SETUP_PATH || checking) return true;

  checking = true;
  showCheckingGuard();
  try {
    const { data } = await api.get("/active-interview");
    if (data?.active && data?.session_id) {
      if (resumeRememberedInterviewIfSameSession(data)) return false;
      if (getRememberedActiveInterviewSessionId()) clearRememberedActiveInterviewRoute();
      showActiveGuard(data);
      startRecoveryPolling();
      return false;
    }
    clearRememberedActiveInterviewRoute();
    stopRecoveryPolling();
    hideGuard();
    return true;
  } catch {
    // Authentication and network failures remain owned by the normal app flow.
    // The server-side POST /sessions 409 remains the final concurrency backstop.
    stopRecoveryPolling();
    hideGuard();
    return true;
  } finally {
    checking = false;
  }
}

export function installActiveInterviewSetupGuard() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const scheduleCheck = () => {
    if (normalizedPath() === SETUP_PATH) {
      showCheckingGuard();
      window.setTimeout(() => {
        checkActiveInterviewBeforeSetup();
      }, 0);
    } else {
      stopRecoveryPolling();
      hideGuard();
    }
  };

  for (const method of ["pushState", "replaceState"]) {
    const original = window.history[method];
    window.history[method] = function patchedHistoryMethod(...args) {
      const result = original.apply(this, args);
      scheduleCheck();
      return result;
    };
  }

  window.addEventListener("popstate", scheduleCheck);
}
